// FILE: ThreadDirectoryView.tsx
// Purpose: The "Todos los hilos" section — filters, one page of threads, and the way into each.
// Layer: Route-level screen
// Exports: ThreadDirectoryView
//
// ── What this screen is for ───────────────────────────────────────────────────────────────────
// The sidebar is this person's own work: threads they created or were given. It stopped being
// "everything in the installation" for administrators too, because a list that holds everybody's
// work is a list nobody can find their own work in. This is where the rest lives, and reaching it
// is a deliberate act — a search with filters, gated by `threads.view_all`.
//
// Opening a row does not adopt the thread. It streams into the chat surface for as long as it is
// on screen and never appears in the reader's sidebar; see `AppState.foreignThreadIds`.

import type { ProjectId, ThreadDirectoryEntry, UserId } from "@taskin-code/contracts";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import { RefreshCwIcon, SearchIcon, UsersIcon } from "~/lib/icons";
import {
  DEFAULT_THREAD_DIRECTORY_SORT,
  DEFAULT_THREAD_DIRECTORY_STATE,
  THREAD_DIRECTORY_SORTS,
  THREAD_DIRECTORY_STATES,
  toThreadDirectoryInput,
  type ThreadDirectoryRouteSearch,
  type ThreadDirectoryStateFilter,
} from "~/lib/threadDirectoryRouteSearch";
import { threadDirectoryQueryOptions } from "~/lib/threadDirectoryReactQuery";
import { useUserDirectory } from "~/lib/userDirectoryReactQuery";
import { useStore } from "~/store";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { SidebarHeaderTrigger, SidebarInset } from "~/components/ui/sidebar";
import { ThreadDirectoryRow, ThreadDirectoryRowSkeleton } from "./ThreadDirectoryRow";

const ALL_VALUE = "__all__";
const SKELETON_ROW_KEYS = ["1", "2", "3", "4", "5", "6"] as const;

const STATE_LABELS: Record<ThreadDirectoryStateFilter, string> = {
  active: "Activos",
  running: "Trabajando ahora",
  archived: "Archivados",
  all: "Todos los estados",
};

const SORT_LABELS: Record<(typeof THREAD_DIRECTORY_SORTS)[number], string> = {
  activity: "Última actividad",
  created: "Fecha de creación",
};

const PR_LABELS = {
  [ALL_VALUE]: "Con y sin PR",
  with: "Con PR",
  without: "Sin PR",
} as const;

/**
 * How long the search box waits before it becomes a query.
 *
 * Every keystroke would otherwise be a full-text search across every message in the installation.
 * Long enough to cover typing a word, short enough that finishing one and looking up feels answered.
 */
const SEARCH_DEBOUNCE_MS = 350;

export function ThreadDirectoryView(props: { fixedProjectId?: ProjectId } = {}) {
  const routeSearch = useSearch({ strict: false }) as ThreadDirectoryRouteSearch;
  const search = useMemo(
    () =>
      props.fixedProjectId ? { ...routeSearch, projectId: props.fixedProjectId } : routeSearch,
    [props.fixedProjectId, routeSearch],
  );
  const navigate = useNavigate();
  const projects = useStore((store) => store.projects);
  const userDirectory = useUserDirectory();

  // The box is local state and the URL is what the query reads: typing has to feel immediate while
  // the request behind it lags a beat. They are reconciled by the debounce below, and by resetting
  // the box whenever the URL changes underneath it (a back button, a fresh link).
  const [queryDraft, setQueryDraft] = useState(search.q ?? "");
  useEffect(() => {
    setQueryDraft(search.q ?? "");
  }, [search.q]);

  const updateSearch = useMemo(
    () =>
      (next: Partial<ThreadDirectoryRouteSearch>, options?: { readonly keepPage?: boolean }) => {
        const merged: ThreadDirectoryRouteSearch = {
          ...search,
          ...next,
          // Any change to a filter invalidates the page: page 4 of the old question says nothing
          // about the new one, and it is the surest way to land on an empty screen.
          ...(options?.keepPage ? {} : { page: undefined }),
        };
        void navigate({
          to: ".",
          replace: true,
          search: {
            ...(merged.q ? { q: merged.q } : {}),
            ...(merged.projectId ? { projectId: merged.projectId } : {}),
            ...(merged.userId ? { userId: merged.userId } : {}),
            ...(merged.state && merged.state !== DEFAULT_THREAD_DIRECTORY_STATE
              ? { state: merged.state }
              : {}),
            ...(merged.pr ? { pr: merged.pr } : {}),
            ...(merged.activityFrom ? { activityFrom: merged.activityFrom } : {}),
            ...(merged.activityTo ? { activityTo: merged.activityTo } : {}),
            ...(merged.createdFrom ? { createdFrom: merged.createdFrom } : {}),
            ...(merged.createdTo ? { createdTo: merged.createdTo } : {}),
            ...(merged.sort && merged.sort !== DEFAULT_THREAD_DIRECTORY_SORT
              ? { sort: merged.sort }
              : {}),
            ...(merged.page && merged.page > 1 ? { page: merged.page } : {}),
          },
        });
      },
    [navigate, search],
  );

  useEffect(() => {
    const normalized = queryDraft.trim();
    if (normalized === (search.q ?? "")) return;
    const timer = window.setTimeout(() => {
      updateSearch({ q: normalized.length > 0 ? normalized : undefined });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [queryDraft, search.q, updateSearch]);

  const input = useMemo(() => toThreadDirectoryInput(search), [search]);
  const listQuery = useQuery(threadDirectoryQueryOptions(input));

  const projectNameById = useMemo(
    () => new Map(projects.map((project) => [project.id, project.name])),
    [projects],
  );
  const usersById = useMemo(
    () => new Map((userDirectory.data ?? []).map((entry) => [entry.id, entry])),
    [userDirectory.data],
  );

  const openThread = (entry: ThreadDirectoryEntry) => {
    void navigate({ to: "/$threadId", params: { threadId: entry.threadId } });
  };

  const state = search.state ?? DEFAULT_THREAD_DIRECTORY_STATE;
  const sort = search.sort ?? DEFAULT_THREAD_DIRECTORY_SORT;
  const page = search.page ?? 1;
  const result = listQuery.data;
  const entries = result?.entries ?? [];
  const total = result?.total ?? 0;
  const perPage = result?.perPage ?? input.perPage;
  const pageCount = total === 0 ? 1 : Math.ceil(total / perPage);
  const hasFilters =
    Boolean(search.q) ||
    Boolean(search.projectId) ||
    Boolean(search.userId) ||
    Boolean(search.pr) ||
    Boolean(search.activityFrom ?? search.activityTo) ||
    Boolean(search.createdFrom ?? search.createdTo) ||
    state !== DEFAULT_THREAD_DIRECTORY_STATE;

  return (
    <SidebarInset className="isolate h-dvh min-h-0 overflow-hidden bg-background">
      <div className="flex h-full flex-col">
        {/* ── Top nav ───────────────────────────────────────────────────── */}
        <div className="flex h-12 shrink-0 items-center gap-3 border-b border-border px-4 sm:px-6">
          <SidebarHeaderTrigger className="size-7 shrink-0" />
          <div className="flex items-center gap-2">
            <UsersIcon className="size-4 text-muted-foreground/70" />
            <h1 className="text-sm font-semibold text-foreground">Todos los hilos</h1>
          </div>
          <div className="ms-auto flex items-center gap-2">
            {total > 0 ? (
              <span className="text-xs text-muted-foreground/72">
                {total} {total === 1 ? "hilo" : "hilos"}
              </span>
            ) : null}
            <Button
              size="xs"
              variant="ghost"
              disabled={listQuery.isFetching}
              onClick={() => void listQuery.refetch()}
              aria-label="Actualizar la lista"
            >
              <RefreshCwIcon
                className={listQuery.isFetching ? "size-3.5 animate-spin" : "size-3.5"}
              />
              Actualizar
            </Button>
          </div>
        </div>

        {/* ── Filters ───────────────────────────────────────────────────── */}
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border/60 px-3 py-2 sm:px-5">
          <div className="relative min-w-56 flex-1">
            <SearchIcon className="pointer-events-none absolute start-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground/60" />
            <Input
              value={queryDraft}
              onChange={(event) => setQueryDraft(event.target.value)}
              placeholder="Buscar en títulos y mensajes…"
              aria-label="Buscar hilos"
              className="h-7 ps-7 text-xs"
            />
          </div>

          <Select
            value={state}
            onValueChange={(value) => {
              if (typeof value !== "string") return;
              updateSearch({ state: value as ThreadDirectoryStateFilter });
            }}
          >
            <SelectTrigger size="xs" className="w-40" aria-label="Filtrar por estado">
              <SelectValue>{STATE_LABELS[state]}</SelectValue>
            </SelectTrigger>
            <SelectPopup align="start" alignItemWithTrigger={false}>
              {THREAD_DIRECTORY_STATES.map((option) => (
                <SelectItem key={option} hideIndicator value={option}>
                  {STATE_LABELS[option]}
                </SelectItem>
              ))}
            </SelectPopup>
          </Select>

          {!props.fixedProjectId ? (
            <Select
              value={search.projectId ?? ALL_VALUE}
              onValueChange={(value) => {
                if (typeof value !== "string") return;
                updateSearch({
                  projectId: value === ALL_VALUE ? undefined : (value as ProjectId),
                });
              }}
            >
              <SelectTrigger size="xs" className="w-44" aria-label="Filtrar por proyecto">
                <SelectValue>
                  {search.projectId
                    ? (projectNameById.get(search.projectId) ?? search.projectId)
                    : "Todos los proyectos"}
                </SelectValue>
              </SelectTrigger>
              <SelectPopup align="start" alignItemWithTrigger={false}>
                <SelectItem hideIndicator value={ALL_VALUE}>
                  Todos los proyectos
                </SelectItem>
                {projects.map((project) => (
                  <SelectItem key={project.id} hideIndicator value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
          ) : null}

          <Select
            value={search.userId ?? ALL_VALUE}
            onValueChange={(value) => {
              if (typeof value !== "string") return;
              updateSearch({ userId: value === ALL_VALUE ? undefined : (value as UserId) });
            }}
          >
            <SelectTrigger size="xs" className="w-44" aria-label="Filtrar por persona">
              <SelectValue>
                {search.userId
                  ? (usersById.get(search.userId)?.displayName ?? search.userId)
                  : "Todas las personas"}
              </SelectValue>
            </SelectTrigger>
            <SelectPopup align="start" alignItemWithTrigger={false}>
              <SelectItem hideIndicator value={ALL_VALUE}>
                Todas las personas
              </SelectItem>
              {(userDirectory.data ?? []).map((user) => (
                <SelectItem key={user.id} hideIndicator value={user.id}>
                  {user.displayName}
                </SelectItem>
              ))}
            </SelectPopup>
          </Select>

          <Select
            value={search.pr ?? ALL_VALUE}
            onValueChange={(value) => {
              if (typeof value !== "string") return;
              updateSearch({
                pr: value === ALL_VALUE ? undefined : (value as "with" | "without"),
              });
            }}
          >
            <SelectTrigger size="xs" className="w-32" aria-label="Filtrar por pull request">
              <SelectValue>{PR_LABELS[search.pr ?? ALL_VALUE]}</SelectValue>
            </SelectTrigger>
            <SelectPopup align="start" alignItemWithTrigger={false}>
              <SelectItem hideIndicator value={ALL_VALUE}>
                {PR_LABELS[ALL_VALUE]}
              </SelectItem>
              <SelectItem hideIndicator value="with">
                {PR_LABELS.with}
              </SelectItem>
              <SelectItem hideIndicator value="without">
                {PR_LABELS.without}
              </SelectItem>
            </SelectPopup>
          </Select>

          <Select
            value={sort}
            onValueChange={(value) => {
              if (typeof value !== "string") return;
              updateSearch({ sort: value as (typeof THREAD_DIRECTORY_SORTS)[number] });
            }}
          >
            <SelectTrigger size="xs" className="w-44" aria-label="Ordenar por">
              <SelectValue>{SORT_LABELS[sort]}</SelectValue>
            </SelectTrigger>
            <SelectPopup align="start" alignItemWithTrigger={false}>
              {THREAD_DIRECTORY_SORTS.map((option) => (
                <SelectItem key={option} hideIndicator value={option}>
                  {SORT_LABELS[option]}
                </SelectItem>
              ))}
            </SelectPopup>
          </Select>
        </div>

        {/* ── Date ranges ───────────────────────────────────────────────── */}
        <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b border-border/60 px-3 py-2 text-xs text-muted-foreground/72 sm:px-5">
          <DayRange
            label="Actividad"
            from={search.activityFrom ?? ""}
            to={search.activityTo ?? ""}
            onChange={(from, to) =>
              updateSearch({
                activityFrom: from.length > 0 ? from : undefined,
                activityTo: to.length > 0 ? to : undefined,
              })
            }
          />
          <DayRange
            label="Creación"
            from={search.createdFrom ?? ""}
            to={search.createdTo ?? ""}
            onChange={(from, to) =>
              updateSearch({
                createdFrom: from.length > 0 ? from : undefined,
                createdTo: to.length > 0 ? to : undefined,
              })
            }
          />
          {hasFilters ? (
            <Button
              size="xs"
              variant="ghost"
              className="ms-auto"
              onClick={() => void navigate({ to: ".", replace: true, search: {} })}
            >
              Limpiar filtros
            </Button>
          ) : null}
        </div>

        {/* ── The page ──────────────────────────────────────────────────── */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          {listQuery.isPending ? (
            <ul className="flex flex-col gap-1.5">
              {SKELETON_ROW_KEYS.map((key) => (
                <ThreadDirectoryRowSkeleton key={key} />
              ))}
            </ul>
          ) : listQuery.isError ? (
            <div className="flex min-h-40 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-destructive/40 bg-destructive/5 px-5 py-6 text-center">
              <div className="max-w-sm space-y-1">
                <p className="text-sm font-medium text-foreground">
                  No se pudo cargar la lista de hilos
                </p>
                <p className="text-xs text-muted-foreground">
                  {listQuery.error instanceof Error
                    ? listQuery.error.message
                    : "Algo falló al hablar con el servidor."}
                </p>
              </div>
              <Button size="xs" variant="outline" onClick={() => void listQuery.refetch()}>
                Reintentar
              </Button>
            </div>
          ) : entries.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-20 text-center">
              <SearchIcon className="size-8 text-muted-foreground/40" />
              <p className="text-sm font-medium text-foreground">
                {hasFilters ? "Nada coincide con estos filtros" : "Todavía no hay hilos"}
              </p>
              <p className="max-w-sm text-xs text-muted-foreground/72">
                {hasFilters
                  ? "Prueba a ampliar el estado, el rango de fechas o a buscar otra palabra."
                  : "Cuando alguien abra un hilo aparecerá aquí, sea de quien sea."}
              </p>
            </div>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {entries.map((entry) => (
                <ThreadDirectoryRow
                  key={entry.threadId}
                  entry={entry}
                  projectName={projectNameById.get(entry.projectId) ?? null}
                  usersById={usersById}
                  onOpen={openThread}
                />
              ))}
            </ul>
          )}
        </div>

        {/* ── Pagination ────────────────────────────────────────────────── */}
        {pageCount > 1 ? (
          <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border/60 px-4 py-2 sm:px-6">
            <span className="text-xs text-muted-foreground/72">
              Página {page} de {pageCount}
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="xs"
                variant="outline"
                disabled={page <= 1}
                onClick={() =>
                  updateSearch({ page: page - 1 <= 1 ? undefined : page - 1 }, { keepPage: true })
                }
              >
                Anterior
              </Button>
              <Button
                size="xs"
                variant="outline"
                disabled={!(result?.hasMore ?? false)}
                onClick={() => updateSearch({ page: page + 1 }, { keepPage: true })}
              >
                Siguiente
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </SidebarInset>
  );
}

/**
 * A from/to pair of calendar days.
 *
 * Native date inputs rather than a picker component: this is an operator's filter, the browser's
 * own control already speaks the reader's locale and keyboard, and it is the one place in the app
 * where a date is typed rather than chosen from what happened.
 */
function DayRange(props: {
  readonly label: string;
  readonly from: string;
  readonly to: string;
  readonly onChange: (from: string, to: string) => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="shrink-0">{props.label}</span>
      <Input
        type="date"
        value={props.from}
        aria-label={`${props.label}: desde`}
        onChange={(event) => props.onChange(event.target.value, props.to)}
        className="h-7 w-36 text-xs"
      />
      <span aria-hidden>–</span>
      <Input
        type="date"
        value={props.to}
        aria-label={`${props.label}: hasta`}
        onChange={(event) => props.onChange(props.from, event.target.value)}
        className="h-7 w-36 text-xs"
      />
    </div>
  );
}
